import React, { useState, useEffect, useRef } from 'react';
import {
  Select, InputNumber, DatePicker, Input, Button, Table,
  message, Popconfirm, Typography, Space, Card, Divider,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import apiService from '../../services/api';

const { Title } = Typography;
const { Option } = Select;
const { MonthPicker } = DatePicker;

const Expenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);

  const [filterMonth, setFilterMonth] = useState(dayjs());

  const [type, setType] = useState(null);
  const [amount, setAmount] = useState(null);
  const [date, setDate] = useState(dayjs());
  const [notes, setNotes] = useState('');

  const [expenseTypes, setExpenseTypes] = useState([]);
  const [newTypeName, setNewTypeName] = useState('');
  const [addingType, setAddingType] = useState(false);
  const newTypeInputRef = useRef(null);

  const fetchExpenses = async (month) => {
    try {
      setLoading(true);
      const params = {
        startDate: month.startOf('month').toISOString(),
        endDate: month.endOf('month').toISOString(),
      };
      const res = await apiService.expenses.getAll(params);
      setExpenses(res.data || []);
    } catch {
      message.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchExpenses(filterMonth); }, [filterMonth]);

  const fetchTypes = async () => {
    try {
      const res = await apiService.expenseTypes.getAll();
      setExpenseTypes(res.data || []);
    } catch {
      message.error('Failed to load expense types');
    }
  };

  useEffect(() => { fetchTypes(); }, []);

  const handleAddType = async () => {
    if (!newTypeName.trim()) return;
    try {
      setAddingType(true);
      await apiService.expenseTypes.create(newTypeName.trim());
      setNewTypeName('');
      fetchTypes();
    } catch (e) {
      message.error(e?.response?.data?.message || 'Failed to add type');
    } finally {
      setAddingType(false);
    }
  };

  const handleAdd = async () => {
    if (!type) return message.warning('Please select an expense type');
    if (!amount || amount <= 0) return message.warning('Please enter a valid amount');
    try {
      setAdding(true);
      await apiService.expenses.create({
        type,
        amount,
        date: date.toISOString(),
        notes,
      });
      setType(null);
      setAmount(null);
      setDate(dayjs());
      setNotes('');
      fetchExpenses(filterMonth);
    } catch {
      message.error('Failed to add expense');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await apiService.expenses.delete(id);
      setExpenses(prev => prev.filter(e => e._id !== id));
    } catch {
      message.error('Failed to delete');
    }
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 130,
      render: (d) => dayjs(d).format('DD MMM YYYY'),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 130,
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      align: 'right',
      render: (v) => `£${Number(v).toFixed(2)}`,
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'notes',
      render: (v) => v || '—',
    },
    {
      title: '',
      key: 'action',
      width: 60,
      align: 'center',
      render: (_, record) => (
        <Popconfirm title="Delete this expense?" onConfirm={() => handleDelete(record._id)} okText="Delete" okType="danger">
          <Button type="text" danger icon={<DeleteOutlined />} size="small" />
        </Popconfirm>
      ),
    },
  ];

  const total = expenses.reduce((s, e) => s + (e.amount || 0), 0);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <Title level={3} style={{ margin: 0 }}>Expenses</Title>
        <MonthPicker
          value={filterMonth}
          onChange={(m) => m && setFilterMonth(m)}
          allowClear={false}
          format="MMM YYYY"
        />
      </div>

      {/* Add form */}
      <Card style={{ marginBottom: 24 }}>
        <Space wrap>
          <Select
            placeholder="Expense type"
            value={type}
            onChange={setType}
            style={{ width: 180 }}
            dropdownRender={(menu) => (
              <>
                {menu}
                <Divider style={{ margin: '6px 0' }} />
                <Space style={{ padding: '4px 8px' }}>
                  <Input
                    ref={newTypeInputRef}
                    size="small"
                    placeholder="New type name"
                    value={newTypeName}
                    onChange={(e) => setNewTypeName(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                    style={{ width: 150 }}
                  />
                  <Button
                    type="text"
                    size="small"
                    icon={<PlusOutlined />}
                    loading={addingType}
                    onClick={handleAddType}
                  >
                    Add
                  </Button>
                </Space>
              </>
            )}
          >
            {expenseTypes.map(t => <Option key={t._id} value={t.name}>{t.name}</Option>)}
          </Select>
          <InputNumber
            placeholder="Amount (£)"
            value={amount}
            onChange={setAmount}
            min={0}
            precision={2}
            prefix="£"
            style={{ width: 150 }}
          />
          <DatePicker
            value={date}
            onChange={(d) => d && setDate(d)}
            format="DD MMM YYYY"
            allowClear={false}
          />
          <Input
            placeholder="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            style={{ width: 220 }}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
            loading={adding}
          >
            Add
          </Button>
        </Space>
      </Card>

      {/* List */}
      <Table
        dataSource={expenses}
        columns={columns}
        rowKey="_id"
        loading={loading}
        pagination={{ pageSize: 20, hideOnSinglePage: true }}
        size="small"
        summary={() => expenses.length > 0 && (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={2}><strong>Total</strong></Table.Summary.Cell>
            <Table.Summary.Cell index={2} align="right"><strong>£{total.toFixed(2)}</strong></Table.Summary.Cell>
            <Table.Summary.Cell index={3} colSpan={2} />
          </Table.Summary.Row>
        )}
      />
    </div>
  );
};

export default Expenses;
